"use client";

import { Citrus, FlaskConical, Barrel, Wine } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "ASSESS",
  Icon: Citrus,
  description: "We begin with a detailed site evaluation, identifying invasive species and mapping the terrain to determine the most efficient clearing path."
}, {
  number: "02",
  title: "MULCH",
  Icon: FlaskConical,
  description: "Our high-performance forestry mulchers grind standing trees and brush into nutrient-rich organic matter, instantly stabilizing the soil."
}, {
  number: "03",
  title: "REFINE",
  Icon: Barrel,
  description: "We meticulously clear remaining debris and level the ground, ensuring a clean, park-like finish that is ready for immediate use or planting."
}, {
  number: "04",
  title: "RESTORE",
  Icon: Wine,
  description: "The final result is a healthy, accessible landscape. We leave your property transformed, with improved aesthetics and reduced fire risk."
}];
export default function HowLandClearingIsAchieved() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3883" id="how-land-clearing-is-achieved" className="bg-background text-foreground relative overflow-hidden py-32 md:py-[200px]">
      {}
      <div className="pointer-events-none absolute top-0 left-4 hidden h-full lg:left-8 lg:block">
        <div className="sticky top-32">
          <div className="font-sans text-[10px] font-medium tracking-[0.3em] uppercase text-muted-foreground" style={{
          writingMode: "vertical-rl"
        }}>
            00 &mdash; THE PROCESS
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-20">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="md:col-span-7">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-primary" />
              <span className="font-sans text-xs font-medium tracking-[0.25em] uppercase text-primary">
                Operational Excellence
              </span>
            </div>
            <h2 className="font-default text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-[64px]">
              Precision clearing
              <br />
              <span className="text-secondary">
                for a healthier forest.
              </span>
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-6">
            <p className="font-default text-lg leading-relaxed text-foreground/80 md:text-xl">
              At Chipper Mulching Co, we believe in land management that respects the ecosystem. Our forestry mulching technique eliminates the need for burning or hauling, returning nutrients directly to your soil.
            </p>
            <p className="mt-5 font-default text-base leading-relaxed text-muted-foreground">
              No massive burn piles. No scarred earth. Just a professional, efficient transformation of your overgrown property into usable space.
            </p>
          </div>
        </div>

        {}
        <div className="relative mt-20 md:mt-28">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {steps.map((step, i) => {
            const card = useRevealOnScroll<HTMLDivElement>({
              threshold: 0.15
            });
            const Icon = step.Icon;
            return <div key={step.number} data-index={i} ref={card.ref} className={`group relative transition-all duration-700 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  {}
                  {i < steps.length - 1 && <div className="pointer-events-none absolute top-1/2 -right-3 z-20 hidden lg:block">
                      <div className="flex items-center gap-0.5">
                        <span className="h-px w-1.5 bg-primary" />
                        <span className="h-px w-1 bg-primary/60" />
                        <span className="h-px w-0.5 bg-primary/30" />
                      </div>
                    </div>}

                  {}
                  <div className="relative h-[380px] overflow-hidden border border-border bg-card shadow-md transition-all duration-500 group-hover:-translate-y-1 group-hover:border-primary group-hover:shadow-xl rounded-lg">
                    {}
                    <div className="absolute -top-4 -left-2 select-none">
                      <span className="font-sans text-[120px] font-extrabold leading-none tracking-tighter text-muted/30 transition-colors duration-[400ms] group-hover:text-primary/10" aria-hidden="true">
                        {step.number}
                      </span>
                    </div>

                    {}
                    <div className="relative flex h-full flex-col justify-between p-8 pt-32">
                      <Icon className="h-10 w-10 text-secondary transition-colors duration-300 group-hover:text-primary" strokeWidth={1.5} />

                      <div>
                        <div className="mb-4 h-px w-8 bg-border transition-all duration-500 group-hover:w-16 group-hover:bg-primary" />
                        <h3 className="font-default text-2xl font-bold tracking-tight text-foreground">
                          {step.title}
                        </h3>
                        <p className="mt-4 font-default text-[15px] leading-relaxed text-muted-foreground">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    {}
                    <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
                    </div>
                  </div>
                </div>;
          })}
          </div>

          {}
          <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-border pt-8 sm:flex-row sm:items-center">
            <p className="font-sans text-xs tracking-[0.25em] uppercase text-muted-foreground">
              Four steps &mdash; total transformation
            </p>
            <p className="font-default italic text-sm text-secondary">
              Serving residential and commercial properties.
            </p>
          </div>
        </div>
      </div>
    </section>;
}